/**
 * ContractDetail — read-only tracking view for a single contract record.
 *
 * Polls /contract/<id> every 12s so the rep sees Documenso lifecycle
 * updates (sent → viewed → signed/declined/expired) without a manual
 * refresh. Stops polling once the contract enters a terminal state.
 *
 * Brand: Master Purple for primary chrome + headers, status pills use the
 * shared StatusPill component (which knows about contract statuses).
 */
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { navigate } from '../App'
import { colors } from '../theme'
import StatusPill from '../components/StatusPill'
import {
  contractPdfUrl,
  getContract,
  type ContractRecord,
  type ContractStatus,
} from '../api/contracts'

const TERMINAL: ContractStatus[] = ['signed', 'declined', 'expired', 'void']

const TIMELINE: { status: ContractStatus; label: string }[] = [
  { status: 'draft', label: 'Drafted' },
  { status: 'sent', label: 'Sent to operator' },
  { status: 'viewed', label: 'Viewed by operator' },
  { status: 'signed', label: 'Signed' },
]

export default function ContractDetail({ id }: { id: string }) {
  const { authHeaders, apiBase } = useAuth()
  const [rec, setRec] = useState<ContractRecord | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await getContract(apiBase, authHeaders(), id)
      setRec(r)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Not found')
    }
  }, [apiBase, authHeaders, id])

  useEffect(() => {
    void load()
  }, [load])

  // Poll until terminal. 12s is a balance: fast enough that "operator just
  // signed" updates feel near-realtime, slow enough that we don't burn
  // Firestore reads while a contract sits in 'viewed' for days.
  useEffect(() => {
    if (!rec) return
    if (TERMINAL.includes(rec.status)) return
    const t = setInterval(() => {
      void load()
    }, 12_000)
    return () => clearInterval(t)
  }, [rec, load])

  function downloadPdf() {
    if (!rec) return
    const raw = localStorage.getItem('parkify_sales_portal_auth_v1')
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as { token: string }
      window.open(contractPdfUrl(apiBase, rec.id, parsed.token), '_blank')
    } catch {
      /* ignore */
    }
  }

  if (error) {
    return (
      <div>
        <BackLink />
        <div
          style={{
            marginTop: 18,
            padding: '14px 18px',
            background: colors.warningBg,
            border: `1.5px solid ${colors.warningBorder}`,
            borderRadius: 10,
            color: colors.black,
            fontSize: 13,
          }}
        >
          <span style={{ color: colors.red, fontWeight: 700, marginRight: 6 }}>
            ⚠
          </span>
          {error}
        </div>
      </div>
    )
  }

  if (!rec) {
    return (
      <div>
        <BackLink />
        <div style={{ marginTop: 18, color: colors.textMuted, fontSize: 13 }}>
          Loading…
        </div>
      </div>
    )
  }

  const fmtDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString() : '—'

  return (
    <div>
      <BackLink />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginTop: 16,
          marginBottom: 22,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: '2.5px',
              fontWeight: 700,
              color: colors.purple,
              marginBottom: 6,
              textTransform: 'uppercase',
            }}
          >
            Contract · {rec.id.slice(0, 8).toUpperCase()}
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: '-1px',
              margin: '0 0 8px',
              color: colors.black,
            }}
          >
            {rec.operator_name}
          </h1>
          <div style={{ fontSize: 14, color: colors.textMuted }}>
            {rec.operator_email} · client {rec.client_id} ·{' '}
            {rec.rep_name ?? rec.rep_email}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <StatusPill status={rec.status} />
          {rec.documenso_envelope_url ? (
            <a
              href={rec.documenso_envelope_url}
              target="_blank"
              rel="noreferrer"
              style={btnSecondary}
            >
              View on Documenso ↗
            </a>
          ) : null}
          <button onClick={downloadPdf} style={btnPrimary}>
            Download PDF
          </button>
        </div>
      </div>

      <Section title="Timeline">
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {TIMELINE.map((step) => {
            const done = step.status === 'signed'
              ? rec.status === 'signed'
              : step.status === 'viewed'
                ? rec.status === 'viewed' || rec.status === 'signed'
                : step.status === 'sent'
                  ? rec.status !== 'draft'
                  : true
            const at =
              step.status === 'draft'
                ? rec.created_at
                : step.status === 'sent'
                  ? rec.sent_at
                  : step.status === 'viewed'
                    ? rec.viewed_at
                    : rec.signed_at
            return (
              <li
                key={step.status}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '10px 0',
                  borderBottom: `1px solid ${colors.divider}`,
                  opacity: done ? 1 : 0.5,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: done ? colors.purple : colors.divider,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: done ? 700 : 400,
                    color: colors.black,
                    flex: 1,
                  }}
                >
                  {step.label}
                </span>
                <span style={{ fontSize: 12, color: colors.textMuted }}>
                  {fmtDate(at)}
                </span>
              </li>
            )
          })}
          {rec.status === 'declined' ? (
            <li
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '10px 0',
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: colors.red,
                }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: colors.red }}>
                Declined
                {rec.decline_reason ? ` — ${rec.decline_reason}` : ''}
              </span>
              <span style={{ fontSize: 12, color: colors.textMuted }}>
                {fmtDate(rec.declined_at)}
              </span>
            </li>
          ) : null}
          {rec.status === 'expired' ? (
            <li
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '10px 0',
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: colors.gray,
                }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: colors.gray }}>
                Expired
              </span>
              <span style={{ fontSize: 12, color: colors.textMuted }}>
                {fmtDate(rec.expired_at)}
              </span>
            </li>
          ) : null}
        </ol>
      </Section>

      <Section title="Commercial terms">
        <Grid>
          <Field label="Platform fee">
            {rec.payload.platform_fee_bps / 100}% per transaction
          </Field>
          <Field label="Monthly subscription">
            ${rec.payload.monthly_fee_usd.toLocaleString()} / month
          </Field>
          <Field label="Term">
            {rec.payload.term === 'custom'
              ? rec.payload.term_description ?? 'Custom'
              : rec.payload.term === 'monthly'
                ? 'Month-to-month'
                : rec.payload.term}
          </Field>
          <Field label="Start date">{rec.payload.start_date}</Field>
          <Field label="Operator signer">
            {rec.payload.operator_signer_name}
            {rec.payload.operator_signer_title
              ? ` (${rec.payload.operator_signer_title})`
              : ''}
          </Field>
          <Field label="Template version">
            {rec.template_version ?? '—'}
          </Field>
        </Grid>
      </Section>

      <Section title="Lots covered">
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            fontSize: 13,
            lineHeight: 1.7,
            color: colors.black,
          }}
        >
          {rec.payload.lot_labels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      </Section>

      {rec.proposal_id ? (
        <div
          style={{
            marginTop: 18,
            fontSize: 11,
            color: colors.textDim,
            letterSpacing: '0.4px',
          }}
        >
          Generated from proposal{' '}
          <button
            onClick={() =>
              navigate({ name: 'detail', id: rec.proposal_id ?? '' })
            }
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.purple,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            {rec.proposal_id.slice(0, 8).toUpperCase()} ↗
          </button>
          {' · '}Created {new Date(rec.created_at).toLocaleString()}
          {' · '}Updated {new Date(rec.updated_at).toLocaleString()}
        </div>
      ) : (
        <div
          style={{
            marginTop: 18,
            fontSize: 11,
            color: colors.textDim,
            letterSpacing: '0.4px',
          }}
        >
          Created {new Date(rec.created_at).toLocaleString()} · Updated{' '}
          {new Date(rec.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        padding: 24,
        marginBottom: 18,
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2.5px',
          textTransform: 'uppercase',
          color: colors.purple,
          marginBottom: 14,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  )
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '14px 24px',
      }}
    >
      {children}
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          color: colors.textMuted,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: colors.black }}>{children}</div>
    </div>
  )
}

const btnPrimary: React.CSSProperties = {
  background: colors.purple,
  color: colors.white,
  border: 'none',
  borderRadius: 8,
  padding: '10px 18px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}

const btnSecondary: React.CSSProperties = {
  background: colors.white,
  color: colors.black,
  border: `1px solid ${colors.divider}`,
  borderRadius: 8,
  padding: '10px 16px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  textDecoration: 'none',
}

function BackLink() {
  return (
    <button
      onClick={() => navigate({ name: 'proposals' })}
      style={{
        background: 'transparent',
        border: 'none',
        color: colors.purple,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        padding: 0,
        cursor: 'pointer',
      }}
    >
      ← Back to proposals
    </button>
  )
}
